import * as React from "react";
import { useState, useCallback, useEffect } from "react";
import { Button, Input } from "@fluentui/react-components";
import { fetchFastApi } from "../utils/fastApi";
import { useAuth } from "../contexts/authContext";
export const SignIn: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuth();
  useEffect(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("workspace_id");
    localStorage.removeItem("id_token");
    setAuth({
      user: null,
      loading: false,
      accessToken: null,
      workspace: null,
    });
  }, []);

  const handleEmailSubmit = useCallback(async () => {
    if (!email || !password) {
      setError("Please enter your email and password");
      return;
    }
    try {
      setLoading(true);
      const res = await fetchFastApi({
        body: { email, password },
        method: "POST",
        path: "/auth/",
      });
      if (res.error_message) {
        setError(res.error_message);
        return;
      }
      if (res.access_token) {
        localStorage.setItem("access_token", res.access_token);
        localStorage.setItem("refresh_token", res.refresh_token);
        localStorage.setItem("id_token", res.id_token);
        localStorage.setItem("email", email);
        window.location.reload();
      }
    } catch (e) {
      console.error("Error during email sign-in:", e);
      setError("An error occurred during sign-in. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [password, email]);

  const handleGoogleSignIn = useCallback(async () => {
    try {
      // Open the dialog
      Office.context.ui.displayDialogAsync(
        `${window.location.origin}/google-signin`,
        { height: 80, width: 50, promptBeforeOpen: false },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            console.error("Failed to open dialog:", result.error.message);
            setError("Failed to open sign-in dialog. Please try again.");
            return;
          }

          // Handle messages from dialog
          result.value.addEventHandler(
            Office.EventType.DialogMessageReceived,
            async (args: { message: string; origin?: string } | { error: number }) => {
              if ("error" in args) {
                console.error("Dialog error:", args.error);
                return;
              }
              const message = JSON.parse(args.message);
              if (message.type === "google-signin") {
                try {
                  console.log("Google Sign-In message received:", message);
                  const res = await fetchFastApi({
                    body: { credential: message.credential },
                    method: "POST",
                    path: "/auth/google",
                  });
                  if (res.error_message) {
                    setError(res.error_message);
                    return;
                  }
                  if (res.access_token) {
                    localStorage.setItem("access_token", res.access_token);
                    localStorage.setItem("refresh_token", res.refresh_token);
                    localStorage.setItem("id_token", res.id_token);
                    localStorage.setItem("email", res.email);
                    window.location.reload();
                  }
                } catch (e) {
                  console.error("Error during Google Sign-In:", e);
                  setError("An error occurred during Google Sign-In. Please try again.");
                } finally {
                  result.value.close();
                }
              } else if (message.type === "error") {
                setError(message.message);
                result.value.close();
              }
            }
          );
        }
      );
    } catch (e) {
      console.error("Error initiating Google Sign-In:", e);
      setError("An error occurred while initiating Google Sign-In. Please try again.");
    }
  }, []);

  return (
    <div className="p-4 flex flex-col gap-4 h-screen">
      <img src="assets/logo_bg_white.svg" alt="logo" className="w-20 h-auto" />
      <div>
        <div className="text-2xl font-semibold">Welcome to Midship</div>
        <div className="text-gray-600">Please sign in to continue.</div>
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="email" className="text-sm font-semibold">
          Email
        </label>
        <Input
          type="email"
          id="email"
          placeholder="Email"
          onChange={(e) => setEmail(e.target.value)}
          value={email}
          autoComplete="email"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="password" className="text-sm font-semibold">
          Password
        </label>
        <Input
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type="password"
          id="password"
          placeholder="Password"
          autoComplete="current-password"
        />
      </div>
      <div className="text-red-600 text-sm">{error}</div>
      <Button disabled={loading} onClick={handleEmailSubmit}>
        {loading ? "Signing in..." : "Sign in with Email"}
      </Button>
      <div className="border-t border-gray-200 my-1" />
      <Button disabled={loading} onClick={handleGoogleSignIn}>
        {loading ? "Signing in..." : "Sign in with Google"}
      </Button>
      <div className="border-t border-gray-200 my-1" />
      <div className="text-sm text-gray-600">
        Midship is a tool for AI-powered audit and compliance. We currently require an account to use this add in.
        Please contact us{" "}
        <a href="https://midship.ai/help" className="text-blue-500">
          here
        </a>{" "}
        to inquire about access.
      </div>
    </div>
  );
};
